import Constants from 'expo-constants';
import * as SecureStore from 'expo-secure-store';
import { Dimensions, Platform } from 'react-native';

type ClientEventScalar = string | number | boolean | null;

type InstallContext = {
  installId: string;
  installFirstSeenAt: string;
  isFirstSessionOnDevice: boolean;
};

const INSTALL_ID_KEY = 'observability.installId';
const INSTALL_FIRST_SEEN_AT_KEY = 'observability.installFirstSeenAt';

let installContextPromise: Promise<InstallContext> | null = null;
const sessionId = createId('session');

function createId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function getWebStorage() {
  if (typeof localStorage === 'undefined') return null;
  if (typeof localStorage.getItem !== 'function') return null;
  if (typeof localStorage.setItem !== 'function') return null;
  if (typeof localStorage.removeItem !== 'function') return null;
  return localStorage;
}

async function getStoredValue(key: string): Promise<string | null> {
  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    return storage?.getItem(key) ?? null;
  }

  return SecureStore.getItemAsync(key);
}

async function setStoredValue(key: string, value: string): Promise<void> {
  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (storage) storage.setItem(key, value);
    return;
  }

  await SecureStore.setItemAsync(key, value);
}

function detectDeviceType(): 'phone' | 'tablet' | 'desktop' {
  const windowDimensions = Dimensions.get('window');
  const shortestSide = Math.min(windowDimensions.width, windowDimensions.height);

  if (Platform.OS === 'web') {
    if (windowDimensions.width >= 1024) return 'desktop';
    if (windowDimensions.width >= 768) return 'tablet';
    return 'phone';
  }

  if ('isPad' in Platform && Platform.isPad) {
    return 'tablet';
  }

  return shortestSide >= 768 ? 'tablet' : 'phone';
}

async function resolveInstallContext(): Promise<InstallContext> {
  const [storedInstallId, storedFirstSeenAt] = await Promise.all([
    getStoredValue(INSTALL_ID_KEY),
    getStoredValue(INSTALL_FIRST_SEEN_AT_KEY),
  ]);

  if (storedInstallId && storedFirstSeenAt) {
    return {
      installId: storedInstallId,
      installFirstSeenAt: storedFirstSeenAt,
      isFirstSessionOnDevice: false,
    };
  }

  const installId = storedInstallId ?? createId('install');
  const installFirstSeenAt = storedFirstSeenAt ?? new Date().toISOString();

  await Promise.all([
    setStoredValue(INSTALL_ID_KEY, installId),
    setStoredValue(INSTALL_FIRST_SEEN_AT_KEY, installFirstSeenAt),
  ]);

  return {
    installId,
    installFirstSeenAt,
    isFirstSessionOnDevice: true,
  };
}

function getOsVersion(): string | null {
  if (typeof Platform.Version === 'string') return Platform.Version;
  if (typeof Platform.Version === 'number') return String(Platform.Version);
  return null;
}

function getAppVersion(): string {
  return (
    Constants.expoConfig?.version ??
    Constants.nativeAppVersion ??
    'unknown'
  );
}

export async function getClientEventContext(): Promise<Record<string, ClientEventScalar>> {
  if (!installContextPromise) {
    installContextPromise = resolveInstallContext();
  }

  const installContext = await installContextPromise;

  return {
    installId: installContext.installId,
    installFirstSeenAt: installContext.installFirstSeenAt,
    sessionId,
    isFirstSessionOnDevice: installContext.isFirstSessionOnDevice,
    deviceType: detectDeviceType(),
    osName: Platform.OS,
    osVersion: getOsVersion(),
    appVersion: getAppVersion(),
  };
}
